<template>
    <div>
        <v-card class="users" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('users.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            color="white"
                            class="minw100"
                            outlined
                            @click="openForm()"
                        >
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('general.add') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text class="card-body">
                <v-data-table
                    :headers="headers"
                    :items="users"
                    :mobile-breakpoint="0"
                    disable-sort
                    disable-pagination
                    hide-default-footer
                    class="operations striped"
                >
                    <template v-slot:item="{ item }">
                        <tr>
                            <td>{{ item.username }}</td>
                            <td>{{ item.name }}</td>
                            <td>{{ roles.find(r => r.id === item.role_id).name }}</td>
                            <td>{{ $t(`general.${item.status}`) }}</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item :key="1" @click.stop="openForm(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item :key="2" @click.stop="deleteUser(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon class="error--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="error--text">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <cod-dialog id="user-form" :show.sync="formVisible">
            <!-- Header -->
            <template slot="header">
                <span>{{ !form.id ? $t('users.add_user') : $t('users.edit_user') }}</span>
            </template>

            <!-- Content -->
            <div class="pa-5" slot="content">
                <v-form ref="form">
                    <v-text-field
                        v-model="form.username"
                        :label="$t('general.username')"
                        :rules="form.rules.username"
                        :error-messages="form.errors.username"
                        validate-on-blur
                        autocomplete="off"
                        @input="delete form.errors.username"
                    />

                    <v-text-field
                        v-if="locale === 'me'"
                        v-model="form.hash"
                        v-mask="'AA###AA###'"
                        :label="$t('general.operator_code')"
                        :hint="$t('general.operator_code_description')"
                        :rules="form.rules.hash"
                        :error-messages="form.errors.hash"
                        validate-on-blur
                        autocomplete="off"
                        @input="delete form.errors.hash"
                    />

                    <v-row no-gutters>
                        <v-col cols="12" sm="6" :class="{ 'pr-1': $vuetify.breakpoint.smAndUp }">
                            <v-text-field
                                v-model="form.pin"
                                :label="$t('general.pin')"
                                :rules="form.rules.pin"
                                :error-messages="form.errors.pin"
                                type="password"
                                autocomplete="off"
                                @input="
                                    $refs.pinConfirmation.validate();
                                    delete form.errors.pin;
                                "
                            />
                        </v-col>

                        <v-col cols="12" sm="6" :class="{ 'pl-1': $vuetify.breakpoint.smAndUp }">
                            <v-text-field
                                ref="pinConfirmation"
                                v-model="form.pin_confirmation"
                                :label="$t('general.pin_confirmation')"
                                :rules="form.rules.pin_confirmation"
                                :error-messages="form.errors.pin_confirmation"
                                type="password"
                                autocomplete="off"
                            />
                        </v-col>
                    </v-row>

                    <v-text-field
                        v-model="form.name"
                        :label="$t('users.full_name')"
                        :rules="form.rules.name"
                        :error-messages="form.errors.name"
                        validate-on-blur
                        autocomplete="off"
                    />

                    <v-row no-gutters>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="form.role_id"
                                :label="$t('users.role')"
                                :rules="form.rules.role_id"
                                :error-messages="form.errors.role_id"
                                :items="roles"
                                item-text="name"
                                item-value="id"
                                :class="{ 'pr-1': $vuetify.breakpoint.smAndUp }"
                                validate-on-blur
                            />
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="form.status"
                                :class="{ 'pl-1': $vuetify.breakpoint.smAndUp }"
                                :label="$t('general.status')"
                                :rules="form.rules.status"
                                :error-messages="form.errors.status"
                                :items="statuses"
                                item-value="value"
                                item-text="text"
                                validate-on-blur
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </div>

            <!-- Footer -->
            <template slot="footer">
                <v-btn
                    class="minw100 flex-1 mr-1"
                    color="primary"
                    outlined
                    :disabled="form.busy"
                    @click="formVisible = false"
                >
                    {{ this.$t('general.cancel') }}
                </v-btn>
                <v-btn
                    class="minw100 flex-1 ml-1"
                    color="primary"
                    depressed
                    :disabled="form.busy"
                    :loading="form.busy"
                    @click="saveUser()"
                >
                    {{ this.$t('general.save') }}
                </v-btn>
            </template>
        </cod-dialog>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import CodDialog from '@/components/CodDialog';
import ApiService from '@/services/ApiService';
import HashGen from '@/utilities/HashGen';

export default {
    components: {
        CodDialog
    },

    created() {
        loadData('/admin/load/users').then(data => this.onDataLoaded(data));
    },

    data() {
        return {
            users: [],
            roles: [],

            headers: [
                { text: this.$t('general.username'), value: 'username' },
                { text: this.$t('users.full_name'), value: 'name' },
                { text: this.$t('users.role'), value: 'role' },
                { text: this.$t('general.status'), value: 'status' },
                { text: '' }
            ],

            form: this.createForm(),
            formVisible: false,
        };
    },

    computed: {
        statuses() {
            let statuses = [];
            if (this.appConfig.user.statuses.length > 0) {
                this.appConfig.user.statuses.forEach(status => {
                    statuses.push({
                        value: status,
                        text: this.$t(`general.${status}`)
                    });
                });
            }
            return statuses;
        }
    },

    methods: {
        onDataLoaded(data) {
            this.users = data.users;
            this.roles = data.roles.map(role => {
                // if default role and name translation exists use it
                const transKey = `user_roles.default.${role.name}`
                if(this.$t(transKey) !== transKey && role.id <= 4) {
                    role.name = this.$t(transKey)
                }

                return role
            })
        },

        // Create user form
        createForm(user = null) {
            return new CodForm({
                id: user ? user.id : null,
                username: user ? user.username : null,
                hash: user
                    ? user.hash
                    : locale === 'me' ? null : HashGen.regular(),
                pin: null,
                pin_confirmation: null,
                name: user ? user.name : null,
                status: user ? this.statuses.find(status => status.value === user.status).value : null,
                role_id: user ? user.role_id : null,

                rules: {
                    username: [ CodForm.rule('required') ],
                    hash: [ CodForm.rule('required'), CodForm.rule('min_length', 10) ],
                    name: [ CodForm.rule('required') ],
                    status: [ CodForm.rule('required') ],
                    role_id: [ CodForm.rule('required') ],
                    pin: !user ? [ CodForm.rule('required') ] : [],
                    pin_confirmation: !user
                        ? [ CodForm.rule('required'), CodForm.rule('match', () => this.form.pin) ]
                        : [ CodForm.rule('match', () => this.form.pin) ]
                },
            });
        },

        // Open form to add or edit a user
        openForm(user = null) {
            this.form = this.createForm(user);
            this.formVisible = true;

            this.$nextTick(() => {
                if(this.$refs.form) this.$refs.form.resetValidation();
            });
        },

        // Save new user to the server
        saveUser() {
            const formValid = this.$refs.form.validate();

            if(!formValid) {
                return;
            }

            if (!this.form.id) {
                ApiService.post('/admin/users', this.form).then(response => {
                    this.users.push(response.data);
                    this.$snackbar(this.$t('users.saved'), 'success');
                    this.formVisible = false;
                })
            } else {
                ApiService.patch(`/admin/users/${this.form.id}`, this.form).then(response => {
                    this.users.forEach((item, index, array) => {
                        if (item.id === this.form.id) {
                            this.$set(array, index, response.data);
                        }
                    });
                    this.$snackbar(this.$t('users.updated'), 'success');
                    this.formVisible = false;
                })
            }
        },

        // Delete user from the server
        deleteUser(user) {
            this.$confirm(
                this.$t('users.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    ApiService.delete(`/admin/users/${user.id}`, {}).then(response => {
                        this.users = this.users.filter(item => item.id !== user.id);
                        this.$snackbar(this.$t('users.deleted'), 'success');
                    }).catch(error => {
                        const errors = error.response.data ? error.response.data.errors : null
                        if (errors && errors.length && errors[0].code === 'general') {
                            this.$snackbar(this.$t('users.has_debt'), 'error');
                        }
                    });
                }
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.users {
    max-width: 600px;

    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .card-body {
        margin-top: 16px;
    }
}
</style>
